/**
 * Image Generation Service
 * Handles AI image generation using Gemini API
 */

import { makeAuthenticatedRequest } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface ImageGenerationRequest {
  prompt: string;
  provider?: 'gemini' | 'grok';
}

export interface ImageGenerationResponse {
  success: boolean;
  data?: {
    id: number;
    imageUrl: string;
    prompt: string;
    mimeType: string;
    status: string;
  };
  message?: string;
  error?: string;
}

export interface ImageHistoryItem {
  id: number;
  prompt: string;
  image_url: string;
  mime_type: string;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
}

export interface ImageHistoryResponse {
  success: boolean;
  data?: {
    images: ImageHistoryItem[];
    total: number;
    limit: number;
    offset: number;
  };
  message?: string;
  error?: string;
}

export interface ImageResponse {
  success: boolean;
  data?: ImageHistoryItem;
  message?: string;
  error?: string;
}

/**
 * Generate a new image
 */
export const generateImage = async (prompt: string, provider?: 'gemini' | 'grok'): Promise<ImageGenerationResponse> => {
  return await makeAuthenticatedRequest('/images/generate', {
    method: 'POST',
    body: JSON.stringify({ prompt, provider })
  });
};

/**
 * Get user's image generation history
 */
export const getImageHistory = async (
  limit: number = 20, 
  offset: number = 0
): Promise<ImageHistoryResponse> => {
  return await makeAuthenticatedRequest(
    `/images/history?limit=${limit}&offset=${offset}`, 
    {
      method: 'GET'
    }
  );
};

/**
 * Get a specific image by ID
 */
export const getImage = async (id: number): Promise<ImageResponse> => {
  return await makeAuthenticatedRequest(`/images/${id}`, {
    method: 'GET'
  });
};

/**
 * Delete an image generation record
 */
export const deleteImage = async (id: number): Promise<{ success: boolean; message?: string }> => {
  return await makeAuthenticatedRequest(`/images/${id}`, {
    method: 'DELETE'
  });
};

/**
 * Download image file from Firebase URL to user's device
 */
export const downloadImageFile = (imageUrl: string, filename: string = 'image.png'): void => {
  const link = document.createElement('a');
  link.href = imageUrl;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Base64 image generation response interface
 */
export interface Base64ImageResponse {
  image: string; // base64 encoded image data
  mimeType: string;
}

/**
 * Generate image with previous image support (for image journey)
 */
export interface GenerateImageWithPreviousParams {
  prompt: string;
  provider?: 'gemini' | 'grok';
  previousImageData?: string; // base64 encoded previous image (optional)
  previousImageMimeType?: string;
}

/**
 * Generate a base64 image (for preview before entity-specific upload)
 * Uses /ai/generate-image endpoint
 */
export const generateImageBase64 = async (prompt: string, provider?: 'gemini' | 'grok'): Promise<Base64ImageResponse> => {
  const result = await makeAuthenticatedRequest('/ai/generate-image', {
    method: 'POST',
    body: JSON.stringify({ prompt, provider })
  });

  if (!result.success) {
    throw new Error(result.message || result.error || 'Failed to generate image');
  }

  // Backend returns data at root level or nested under 'data'
  const imageData = result.data?.image ? result.data.image : result.image;
  const mimeType = result.data?.mimeType ? result.data.mimeType : result.mimeType;

  if (!imageData || !mimeType) {
    console.error('Invalid response structure:', result);
    throw new Error('Invalid image data received from server');
  }

  return {
    image: imageData,
    mimeType: mimeType
  };
};

/**
 * Generate image with previous image support (for image journey)
 * Uses /ai/generate-image endpoint with previous image context
 */
export const generateImageWithPrevious = async (
  params: GenerateImageWithPreviousParams
): Promise<Base64ImageResponse> => {
  const { prompt, previousImageData, previousImageMimeType, provider } = params;

  const result = await makeAuthenticatedRequest('/ai/generate-image', {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      provider,
      previousImageData,
      previousImageMimeType
    })
  });

  if (!result.success) {
    throw new Error(result.message || result.error || 'Failed to generate image');
  }

  // Backend returns data at root level or nested under 'data'
  const imageData = result.data?.image ? result.data.image : result.image;
  const mimeType = result.data?.mimeType ? result.data.mimeType : result.mimeType;

  if (!imageData || !mimeType) {
    console.error('Invalid response structure:', result);
    throw new Error('Invalid image data received from server');
  }

  return {
    image: imageData,
    mimeType: mimeType
  };
};

/**
 * Generate image for a lesson (returns base64 for preview)
 * Uses /ai/generate-image endpoint (entity validation happens during upload)
 */
export const generateImageForLesson = async (
  lessonId: string,
  prompt: string,
  provider?: 'gemini' | 'grok'
): Promise<Base64ImageResponse> => {
  // Use the generic base64 generation endpoint
  // Entity validation happens when uploading the image
  return await generateImageBase64(prompt, provider);
};

/**
 * Generate image for a category (returns base64 for preview)
 * Uses /ai/generate-image endpoint (entity validation happens during upload)
 */
export const generateImageForCategory = async (
  categoryId: string,
  prompt: string,
  provider?: 'gemini' | 'grok'
): Promise<Base64ImageResponse> => {
  // Use the generic base64 generation endpoint
  // Entity validation happens when uploading the image
  return await generateImageBase64(prompt, provider);
};

/**
 * Generate image for an object (returns base64 for preview)
 * Uses /ai/generate-image endpoint
 */
export const generateImageForObject = async (
  objectId: string,
  prompt: string,
  provider?: 'gemini' | 'grok'
): Promise<Base64ImageResponse> => {
  // Use the generic base64 generation endpoint
  return await generateImageBase64(prompt, provider);
};

/**
 * Upload place image to Firebase Storage
 * @param placeId - The place ID
 * @param file - The image file to upload
 * @returns The download URL of the uploaded image
 */
export const uploadPlaceImage = async (
  placeId: number,
  file: File
): Promise<string> => {
  try {
    // Upload to Firebase Storage
    const { storage } = await import('../firebase/config');
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    const extension = file.name.split('.').pop() || 'png';
    const fileName = `${timestamp}_${randomId}.${extension}`;
    const storagePath = `places/${placeId}/images/${fileName}`;

    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, { contentType: file.type });

    // Get public URL
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading place image:', error);
    throw error;
  }
};

/**
 * Upload object image to Firebase Storage and update object in place properties
 * @param placeId - The place ID that contains the object
 * @param objectId - The object ID
 * @param imageData - Base64 encoded image data
 * @param mimeType - MIME type of the image
 * @param prompt - The prompt used to generate the image
 * @returns The download URL of the uploaded image
 */
export const uploadObjectImage = async (
  placeId: number,
  objectId: string,
  imageData: string,
  mimeType: string,
  prompt: string
): Promise<string> => {
  try {
    // Convert base64 to blob
    const base64Response = await fetch(`data:${mimeType};base64,${imageData}`);
    const blob = await base64Response.blob();

    // Upload to Firebase Storage
    const { storage } = await import('../firebase/config');
    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    const extension = mimeType.split('/')[1] || 'png';
    const fileName = `${timestamp}_${randomId}.${extension}`;
    const storagePath = `places/${placeId}/objects/${objectId}/images/${fileName}`;

    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, blob, { contentType: mimeType });

    // Get public URL
    const downloadURL = await getDownloadURL(storageRef);

    // Note: The caller should update the object in place properties via onObjectUpdate
    // This function only handles the Firebase upload and returns the URL
    return downloadURL;
  } catch (error) {
    console.error('Error uploading object image:', error);
    throw error;
  }
};

